import { DATE_TRUNC_FUNCTION_NAME, DateBucketValueType, DateCondition, EXPLODE_FUNCTION_NAME, Filter } from '@fabric-ui/components';
import { SortType } from '@fabric-ui/components/Table';

export enum SortDir {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const SortDirToSortType = {
  [SortDir.ASC]: SortType.ASC,
  [SortDir.DESC]: SortType.DESC,
} as const satisfies Record<SortDir, SortType>;

export const SortTypeToSortDir = {
  [SortType.ASC]: SortDir.ASC,
  [SortType.DESC]: SortDir.DESC,
} as const satisfies Record<SortType, SortDir>;

export type DateTruncExpression = {
  function: {
    name: typeof DATE_TRUNC_FUNCTION_NAME;
    args: [
      {
        symbol: {
          ref: string;
        };
      },
      {
        stringLiteral: {
          value: DateBucketValueType;
        };
      },
    ];
  };
};

export type ExplodeExpression = {
  function: {
    name: typeof EXPLODE_FUNCTION_NAME;
    args: [
      {
        symbol: {
          ref: string;
        };
      },
    ];
  };
};

export type ExpressionTypes = DateTruncExpression | ExplodeExpression;

export interface Field {
  name: string;
  alias?: string;
  type?: string;
  expression?: ExpressionTypes;
}
export interface QueryObjectMetric extends Field {
  conf?: MetricConfiguration;
}
export interface MetricConfiguration {
  percentage?: { breakdown?: boolean };
  pivot?: {
    metrics: Field[];
    pivotDimension: Field;
    enableRowTotals: boolean;
    pivotFilter: {
      pivotDimApproxTopK: {
        k: number;
      };
    };
  };
}

export interface Select {
  dims: Field[];
  metrics: QueryObjectMetric[];
}

export interface TopResults {
  size?: number;
  groupOthers: boolean;
  offset?: number;
}

type SortPath = {
  path: { name: string }[];
};

export interface SortBy {
  name: string;
  dir: SortDir;
  path?: SortPath;
}

export interface ProjectionID {
  name: string;
  builtIn: boolean;
}

export type FeatureFlagsOverrides = {
  binnedTimeBuckets?: boolean;
  queryAggIfCollapseOptimization?: boolean;
};

export interface DwQueryRequest {
  timezoneOffset?: number;
  timezone?: string;
  select: Select;
  filter?: Filter;
  groupBy?: string[];
  sorting: SortBy[];
  top?: TopResults;
  projectionId: ProjectionID;
  distinct?: boolean;
  timeRange?: DateCondition;
  metricsFilterThreshold?: number;
  featureFlagsOverrides?: FeatureFlagsOverrides;
  metricFilterRootProj?: boolean;
}

export type AliasResolution = { fieldName: string; aliases: string[] }[];

// Example usage (change here every time so it's clear, include optional vars:
export const dwQuery: DwQueryRequest = {
  select: {
    dims: [{ name: 'dim1' }, { name: 'dim2', alias: 'alias2' }],
    metrics: [{ name: 'meas1' }, { name: 'meas2', alias: 'alias4' }],
  },
  groupBy: ['group1', 'group2'],
  sorting: [
    { name: 'field1', dir: SortDir.ASC },
    { name: 'field2', dir: SortDir.DESC },
  ],
  top: {
    size: 10,
    groupOthers: true,
    offset: 0,
  },
  projectionId: {
    name: 'tickets',
    builtIn: false,
  },
  distinct: false,
};

export const dwQueryDefault: DwQueryRequest = {
  select: {
    dims: [],
    metrics: [],
  },
  groupBy: [],
  sorting: [],
  top: {
    size: 100,
    groupOthers: false,
  },
  projectionId: {
    name: 'tickets',
    builtIn: true,
  },
  distinct: true,
  metricsFilterThreshold: 0,
};

export const getDWQueryDefault = (): DwQueryRequest => structuredClone(dwQueryDefault);

export const groupOthersSizeMax = 20;

export const interactiveQueryMaxRowsSize = 40_000;
export type QueryResponse = { data?: any; totals?: Record<string, number>; totalRowCount?: number };
export enum FieldType {
  dim,
  metric,
}
